/* EqDialog class
 * Helper class to provide Fancybox create_dialogality using the native HTML dialog element
 *
 * See MDN documentation for details on how the native element works
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog
 */

const eq_dialog_defaults = {
    selector: '.eq-dialog',
    show_close_button: true,
    close_button_text: 'Close dialog',
    // remove_inline_content: takes inline divs used for content and removes duplicates
    remove_inline_content: true,
    style: {
        width: '800px',
    }
}
// Default HTML template for dialog content
eq_dialog_defaults.template_inner = `<div class="eq-dialog-inner"></div>`;
eq_dialog_defaults.template_close_button = `<form class="eq-close-btn" method="dialog"><button autofocus aria-label="{{close_btn_txt}}"></button></form>`;
eq_dialog_defaults.template_video = `<video class="eq-dialog-video" controls autoplay>` +
    `<source src="{{src}}" type="{{format}}" />` +
    'Sorry, your browser doesn\'t support embedded videos, <a href="{{src}}">download</a> and watch with your favorite video player!' +
    `</video>`;

// Track whether the user is navigating via keyboard vs pointer.
let eqDialogLastInteractionWasKeyboard = false;
let eqDialogModalityListenersAttached = false;

class EqDialog {
    constructor(options = {}) {
        this.settings = JSON.parse(JSON.stringify(eq_dialog_defaults));
        // Override defaults if provided
        for (const prop in options) {
            this.settings[prop] = options[prop];
            if (prop === 'style') {
                for (const style_name in options.style) {
                    this.settings.style[style_name] = options.style[style_name];
                }
            }
        }
        
        // Listen for tab press, if modal is open keep focus locked
        document.addEventListener('keydown', (e) => {
            let dialog = document.querySelector('dialog[open]');
            if (!dialog) return;
            
            // Dynamically find all focusable elements and determine the first and last one
            let dialog_focusables = dialog.querySelectorAll('button,input,select,textarea,iframe,area,a,*[tabindex="0"]');
            let first_focus = dialog_focusables[0];
            let last_focus = dialog_focusables[dialog_focusables.length - 1];

            if (!e.shiftKey && e.key === 'Tab' && document.activeElement === last_focus) {
                e.preventDefault();
                first_focus.focus();
            } else if (e.shiftKey && e.key === 'Tab' && document.activeElement === first_focus) {
                e.preventDefault();
                last_focus.focus();
            }
        });

        // Attach modality listeners once per page load.
        if (!eqDialogModalityListenersAttached) {
            eqDialogModalityListenersAttached = true;
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Tab') eqDialogLastInteractionWasKeyboard = true;
            }, true);
            document.addEventListener('pointerdown', () => {
                eqDialogLastInteractionWasKeyboard = false;
            }, true);
        }
    }

    // Initialize dialog buttons and set up event listeners
    init() {
        const dialog_btns = document.querySelectorAll(this.settings.selector);
        dialog_btns.forEach((el) => {
            // Slick (infinite) clones duplicate triggers with the same href; only init the real slide
            // or create_dialog removes the inline target on first match and later inits fail.
            if (el.closest && el.closest('.slick-cloned')) {
                return;
            }

            let dialog_type, dialog_id, href, src;
            dialog_type = el.dataset.type || false;
            href = el.getAttribute('href');
            src = href || el.dataset.target || false;

            if (!src) {
                console.error(`Could not determine the dialog source. Please set the attribute 'href' or 'data-target'`);
                return;
            }

            if (!dialog_type) {
                dialog_type = this.inferDialogType(src);
                if (!dialog_type) return;
            }

            dialog_id = dialog_type === 'inline' && src ? src.substring(1) : this.generate_dialog_id(dialog_type);

            if (href) el.setAttribute('href', `#${dialog_id}`);

            const dlg = this.create_dialog({
                dialog_type: dialog_type,
                dialog_id: dialog_id,
                src: src,
                img_alt: el.dataset.imgAlt || '',
                close_button_text: el.dataset.closeBtnTxt || this.settings.close_button_text
            });

            if (dlg) {
                dlg.addEventListener('close', () => {
                    const trigger = el;

                    // Pointer users: clear focus so "focus/active" styles don't linger.
                    // Keyboard users: restore logical focus to the trigger (or fallback).
                    if (!el.__eqDialogOpenedWithKeyboard) {
                        // Allow the browser's native focus restoration to run first, then blur.
                        window.setTimeout(() => {
                            if (trigger && typeof trigger.blur === 'function') trigger.blur();
                        }, 0);
                        return;
                    }

                    const fallback = el.__eqDialogPreviouslyFocusedElement;
                    const focusTarget = (trigger && document.contains(trigger)) ? trigger : fallback;
                    if (focusTarget && typeof focusTarget.focus === 'function') {
                        try {
                            focusTarget.focus({ preventScroll: true });
                        } catch (e) {
                            focusTarget.focus();
                        }
                    }
                });
            }

            el.addEventListener('click', (e) => {
                e.preventDefault();
                if (!dlg) return;

                // Store focus + modality at open time for close restoration.
                el.__eqDialogPreviouslyFocusedElement = document.activeElement;
                el.__eqDialogOpenedWithKeyboard = eqDialogLastInteractionWasKeyboard;

                document.body.appendChild(dlg);
                dlg.showModal();
                window.location.hash = dialog_id;
            });
        });
    }

    // Modified `open` function to accept custom `close_button_text`
    open(dialog_type, source, img_alt = '', close_button_text = this.settings.close_button_text) {
        if ((dialog_type === 'inline' && !source) || (!source && (dialog_type === 'image' || dialog_type === 'iframe' || dialog_type === 'video'))) {
            console.error(`${dialog_type} dialogs must have a source.`);
            return;
        }

        const dlg = this.create_dialog({
            dialog_type: dialog_type,
            dialog_id: dialog_type === 'inline' ? source : this.generate_dialog_id(dialog_type),
            src: dialog_type === 'inline' ? '' : source,
            img_alt: img_alt,
            close_button_text: close_button_text  // Pass the custom close button text
        });

        if (!dlg) return;

        document.body.appendChild(dlg);
        dlg.showModal();
        window.location.hash = dialog_type === 'inline' ? source : dlg.id;
        return dlg;
    }

    create_dialog(params = {}) {
        const settings = this.settings;
        const dialog = document.createElement('dialog');
        dialog.classList.add('eq-dialog-wrapper');
        dialog.id = params.dialog_id;
        dialog.innerHTML = settings.template_inner;

        let dialog_content = '';
        if (settings.show_close_button) {
            let close_btn_html = settings.template_close_button.replace('{{close_btn_txt}}', params.close_button_text);
            dialog_content += close_btn_html;
        }

        if (params.dialog_type === 'inline') {
            const inline_div = document.getElementById(params.dialog_id);
            if (!inline_div) {
                console.error(`Unable to find element with ID '${params.dialog_id}'`);
                return false;
            }
            dialog_content += inline_div.innerHTML;
            if (settings.remove_inline_content) inline_div.remove();
            else inline_div.style.display = 'none';
        } else if (params.dialog_type === 'image') {
            dialog_content += `<img src="${params.src}" alt="${params.img_alt}">`;
        } else if (params.dialog_type === 'video') {
            const format = 'video/' + (params.src.match(/\.(\w+)$/) || [])[1];
            dialog_content += settings.template_video.replace('{{src}}', params.src).replace('{{format}}', format);
        } else if (params.dialog_type === 'iframe') {
            dialog_content += `<iframe src="${params.src}"></iframe>`;
        }

        const inner_el = dialog.querySelector('.eq-dialog-inner');
        for (const style_name in settings.style) {
            inner_el.style[style_name] = settings.style[style_name];
        }
        inner_el.innerHTML = dialog_content;

        dialog.addEventListener('close', () => {
            dialog.close();
            if (window.location.hash === `#${dialog.id}`) {
                history.pushState("", document.title, window.location.pathname + window.location.search);
            }
        });

        return dialog;
    }

    generate_dialog_id(dialog_type) {
        let index = 0;
        let id;
        do {
            id = `${dialog_type}-dialog-${index++}`;
        } while (document.getElementById(id));
        return id;
    }

    inferDialogType(src) {
        if (/\.(jp(e|g|eg)|gif|png|bmp|webp|svg|ico)$/i.test(src)) return 'image';
        if (/\.(mp4|mov|ogg|webm)$/i.test(src)) return 'video';
        if (/\.(pdf)$/i.test(src)) return 'iframe';
        if (src[0] === '#') return 'inline';
        console.error(`Could not determine the dialog type for ${src}. Please specify 'data-type' or 'href'.`);
        return null;
    }
}


